import { useEffect, useRef } from "react";
import { TrackingPanel } from "./TrackingPanel";

interface TrackingHistoryDialogProps {
  /** Corriere assegnato (es. "SDA"). Vuoto/assente → "—". */
  carrier: string;
  /** Codice tracking master (LDV) dell'ordine. Vuoto = nessuna LDV. */
  trackingCode: string;
  /** Codici dei colli (collo) della spedizione. Possono coincidere col
   *  master per spedizioni singolo collo: la dedup li unifica. */
  parcelCodes: string[];
  /** Apre un URL nel browser di sistema (openExternalUrl del ContextPanel). */
  onOpenExternal: (url: string) => void;
  /** Chiamato alla chiusura (X, Esc, click fuori). */
  onClose: () => void;
}

/**
 * Popup "Storico spedizione" (#7 2026-06-29, REWORK; Piano 4 2026-07-13).
 *
 * GUSCIO modale: scrim ~50%, role=dialog + aria-modal, chiusura con Esc /
 * click fuori / X, focus iniziale sul bottone chiudi, focus-trap su Tab.
 * Tutto il contenuto informativo (fetch tracking, sezioni LDV/colli,
 * timeline, Aggiorna) vive in ``TrackingPanel``, riusato inline anche
 * dalla pagina ordine (ShippingSection).
 */
export function TrackingHistoryDialog({
  carrier,
  trackingCode,
  parcelCodes,
  onOpenExternal,
  onClose,
}: TrackingHistoryDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Esc chiude + focus-trap: tabolando si resta dentro al dialog.
  useEffect(() => {
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      // Il dialog NON è portalato: dalla card lista (TrackingTimeline variant
      // "dot") è renderizzato dentro la riga-card cliccabile di OrderCard. Senza
      // data-no-open + stopPropagation, chiudere dal backdrop (o cliccare il
      // testo del pannello) bollerebbe fino all'onClick della riga → navigazione
      // indesiderata a /ordini/:ref (bug-hunt 2026-07-13). Stesso presidio di
      // RiordinaSostDialog.
      data-no-open
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tracking-history-title"
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-line bg-card shadow-cardOpen"
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-6 py-4">
          <h2 id="tracking-history-title" className="font-serif text-[19px] font-medium text-ink">
            Storico spedizione
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Chiudi storico spedizione"
            className="rounded-sm p-1 text-ink-3 hover:bg-paper hover:text-ink"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <TrackingPanel
            carrier={carrier}
            trackingCode={trackingCode}
            parcelCodes={parcelCodes}
            onOpenExternal={onOpenExternal}
          />
        </div>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      className="h-4 w-4 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
